import { Card, CardBody } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  getCreditFacilityLoanSettingsTableDataRetrieveQueryKey,
  useCreditFacilityLoanCreate,
} from "gen/endpoints/credit-facility/credit-facility";
import { useTransactionCalculationListSuspense } from "gen/endpoints/transactions/transactions";
import { CreditFacilityLoanCreateDataRequest } from "gen/model/creditFacilityLoanCreateDataRequest";
import { DayCountEnum } from "gen/model/dayCountEnum";
import { InterestAccrualGranularityLevelEnum } from "gen/model/interestAccrualGranularityLevelEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { creditFacilityLoanCreateBody } from "gen/schemas/credit-facility/credit-facility";
import React, { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Restricted } from "shared/components/access/Restricted";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { FormBooleanToggleButtonGroup } from "shared/components/button/ToggleButton";
import Form from "shared/components/form/Form";
import { FormErrorAlert } from "shared/components/form/FormErrorAlert";
import { FormFieldPrefix } from "shared/components/form/FormFieldPrefix";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import { FormTextInput } from "shared/components/input/text/TextInput";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { nowAsIsoDate } from "shared/utils/DateUtils";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";

export const CreditFacilityLoanCreate: React.FC = () => {
  const { t } = useTranslation(["creditFacility", "common", "enums"]);
  const { fundFamilyId, creditFacilityId } = useRequiredStringParams();
  const invalidateQueries = useInvalidateQueryKeys();

  const form = useForm<CreditFacilityLoanCreateDataRequest>({
    defaultValues: {
      version_date: nowAsIsoDate(),
      loan: {
        credit_facility_id: creditFacilityId,
      },
    },
    resolver: zodResolver(creditFacilityLoanCreateBody),
  });

  const { mutateAsync: addLoan, error: upsertError } =
    useCreditFacilityLoanCreate();

  const onSubmit = useCallback(
    async (data: CreditFacilityLoanCreateDataRequest) => {
      await addLoan({
        fundFamilyId,
        creditFacilityId,
        data,
      });
    },
    [addLoan, fundFamilyId, creditFacilityId],
  );

  const creditFacilitySettingsRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .setup({})
    .creditFacilities({});

  const navigate = useNavigate();

  const onSuccess = useCallback(async () => {
    await invalidateQueries(
      getCreditFacilityLoanSettingsTableDataRetrieveQueryKey(
        fundFamilyId,
        creditFacilityId,
      ),
    );
    navigate(
      creditFacilitySettingsRoute.creditFacility({ creditFacilityId }).loan({})
        .$,
    );
  }, [
    creditFacilityId,
    creditFacilitySettingsRoute,
    fundFamilyId,
    invalidateQueries,
    navigate,
  ]);

  const { data: calculations } =
    useTransactionCalculationListSuspense(fundFamilyId);

  const onError = () => true;

  return (
    <PageContent contentWidth="wide">
      <PageHeader title="Add loan" />
      <PageBody>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={onSuccess}
          onError={onError}
        >
          <Card variant="form">
            <CardBody>
              <FormErrorAlert error={upsertError} mb="16px" />
              <FormDateInput name="version_date" title="Loan start date" />
              <FormFieldPrefix prefix="loan">
                <FormTextInput
                  name="name"
                  title={t("creditFacility:setup.loan.loanName")}
                />
                <FormTextInput
                  name="loan_draw_and_paydown_increments"
                  title={t(
                    "creditFacility:setup.loan.loanDrawAndPaydownIncrements",
                  )}
                />
                <FormTextInput
                  name="minimum_draw_amount"
                  title={t("creditFacility:setup.loan.minimumDrawdownAmount")}
                />
                <FormTextInput
                  name="minimum_paydown_amount"
                  title={t("creditFacility:setup.loan.minimumPaydownAmount")}
                />
                <FormSelect
                  name="interest_rate_calculation_id"
                  title={t("creditFacility:setup.loan.interestRateCalculation")}
                >
                  {calculations.map(({ id, name }) => (
                    <SelectOption key={`calculation_${id}`} value={id}>
                      {name}
                    </SelectOption>
                  ))}
                </FormSelect>
                <FormSelect
                  name="interest_accrual_granularity"
                  title={t(
                    "creditFacility:setup.loan.interestAccrualGranularity.title",
                  )}
                >
                  {Object.values(InterestAccrualGranularityLevelEnum).map(
                    (level) => (
                      <SelectOption key={level} value={level}>
                        {t(
                          `creditFacility:setup.loan.interestAccrualGranularity.options.${level}`,
                        )}
                      </SelectOption>
                    ),
                  )}
                </FormSelect>
                <FormNumberInput
                  valueType="percent"
                  name="applicable_margin"
                  title={t("creditFacility:setup.loan.applicableMargin")}
                  unsetWhenEmpty={true}
                />
                <FormNumberInput
                  valueType="percent"
                  name="spread"
                  title={t("creditFacility:setup.loan.spread")}
                  unsetWhenEmpty={true}
                />
                <FormNumberInput
                  valueType="percent"
                  name="interest_rate_floor"
                  title={t("creditFacility:setup.loan.interestRateFloor")}
                  unsetWhenEmpty={true}
                />
                <FormNumberInput
                  valueType="percent"
                  name="interest_rate_ceiling"
                  title={t("creditFacility:setup.loan.interestRateCeiling")}
                  unsetWhenEmpty={true}
                />
                <FormNumberInput
                  name="reference_rate_lookback_period"
                  valueType="number"
                  title={t(
                    "creditFacility:setup.loan.referenceRateLookbackPeriod",
                  )}
                  unsetWhenEmpty={true}
                />
                <FormSelect
                  name="rate_day_count"
                  title={t("creditFacility:setup.loan.rateDayCount.title")}
                >
                  {Object.values(DayCountEnum).map((count_type) => (
                    <SelectOption key={count_type} value={count_type}>
                      {t(`enums:InterestDayCountEnum.${count_type}`)}
                    </SelectOption>
                  ))}
                </FormSelect>
                <FormBooleanToggleButtonGroup
                  name="charge_interest_on_drawdown_day"
                  title={t(
                    "creditFacility:setup.loan.chargeInterestOnDrawdown",
                  )}
                />
                <FormBooleanToggleButtonGroup
                  name="charge_interest_on_paydown_day"
                  title={t("creditFacility:setup.loan.chargeInterestOnPaydown")}
                />
              </FormFieldPrefix>
            </CardBody>
          </Card>
          <FormFooter>
            <FormFooterActions>
              <FormCancelButton
                onClick={() =>
                  navigate(
                    creditFacilitySettingsRoute
                      .creditFacility({ creditFacilityId })
                      .loan({}).$,
                  )
                }
              />
              <Restricted permission={PermissionTypeEnum.write}>
                <FormSubmitButton>
                  {t("creditFacility:settings.loans.createLoan")}
                </FormSubmitButton>
              </Restricted>
            </FormFooterActions>
          </FormFooter>
        </Form>
      </PageBody>
    </PageContent>
  );
};
